import { ScaleLinear as d3ScaleLinear } from 'd3';

import { CoordinatesCategoryData, CoordinatesCategoryDatum } from '../../types';

import { ChartSize } from '../../types';
import { ScatterPlotDimensions } from './types';
import { SortingMethod, getSortingMethod } from '../../utils';

export const sizeToDimension = (size: ChartSize | ScatterPlotDimensions): ScatterPlotDimensions => {
  if (typeof size !== 'string') {
    return size;
  }

  switch (size) {
    case ChartSize.small:
      return { width: 600, height: 300, fontSize: 30 };
    case ChartSize.medium:
    default:
      return { width: 750, height: 375, fontSize: 34 };
    case ChartSize.large:
      return { width: 900, height: 450, fontSize: 38 };
  }
};

export const BASE_NUMBER_OF_TICKS = 20;

export const getClosestLineIndex = (value: number, scale: d3ScaleLinear<number, number, never>) => {
  const ticks = scale.ticks(BASE_NUMBER_OF_TICKS);
  const delta = ticks[1] - ticks[0];
  const index = Math.round(value / delta);
  const roundValue = index * delta;
  return [index, roundValue];
};

export const group = (
  data: CoordinatesCategoryData,
  x: d3ScaleLinear<number, number, never>,
  y: d3ScaleLinear<number, number, never>
) => {
  const groupDict: Record<
    string,
    {
      category: string;
      x: number;
      y: number;
      data: CoordinatesCategoryDatum;
      points: CoordinatesCategoryData;
    }
  > = {};

  data.forEach((d) => {
    const [i, rx] = getClosestLineIndex(d.key[0], x);
    const [j, ry] = getClosestLineIndex(d.key[1], y);

    const key = `${i}_${j}_${d.key[2]}`;
    if (!(key in groupDict)) {
      groupDict[key] = {
        category: d.key[2] as string,
        x: rx,
        y: ry,
        data: {
          key: d.key,
          value: 0,
        },
        points: [],
      };
    }

    groupDict[key].points.push(d);
    groupDict[key].data.value += 1;
  });

  return Object.values(groupDict);
};

export const countBy = (arr: CoordinatesCategoryData, sortingMethod: SortingMethod) => {
  const counts = arr.reduce(
    (prev, curr) => ((prev[curr.key[2] as string] = ++(prev[curr.key[2] as string] as number) || 1), prev),
    {} as Record<string, number>
  );

  return Object.keys(counts)
    .map((key) => ({ key: key, value: counts[key] as number }))
    .sort(getSortingMethod(sortingMethod));
};
